import React, { useContext } from 'react';
import {
  FreeDeposit,
  type FreeDepositProps,
} from 'Components/widgets/payment-info/payment-form/FreeDeposit';
import { Modal } from 'Components/frameworks/modal/Modal';
import { t } from 'Src/i18n';
import { Button } from 'Components/frameworks/button/Button';
import { useDisclosure } from 'Src/hooks/use-disclosure/useDisclosure';
import { useFormikContext } from 'formik';
import type { PaymentFormValues } from 'Src/types/payment';
import { useBookingNavbar } from 'Components/widgets/booking-navbar/useBookingNavbar';
import { useBookingNavbarContext } from 'Components/widgets/booking-navbar/BookingNavbarContext';
import { useAppContext } from 'Context/ReactAppContext';
import { isFeatureFlipped } from 'Utils/feature-flipping/featureFlippingUtils';
import { TemplateString } from 'Components/frameworks/template-string/TemplateString';
import { formatAmount } from 'Utils/currency-config-for-locale/currencyConfigForLocale';
import { PaymentInfoContext } from 'Components/widgets/payment-info/PaymentInfoContext';

const ALLOWED_EXCEEDANCE_PERCENTAGE = 0.5;
const ALLOWED_EXCEEDANCE_AMOUNT = 100000;

export const B2BFreeDeposit = (props: FreeDepositProps) => {
  const { isOpen, onClose, onOpen } = useDisclosure();
  const appContext = useAppContext();
  const { isCxo, locale } = useAppContext();
  const { currency } = useContext(PaymentInfoContext);
  const withExceedancePrice =
    isCxo && isFeatureFlipped(appContext)('exceedTotalAmount')
      ? ALLOWED_EXCEEDANCE_AMOUNT
      : props.price.value + (props.totalPrice.value * ALLOWED_EXCEEDANCE_PERCENTAGE) / 100;
  const { validationCallback } = useBookingNavbarContext();
  const formik = useFormikContext<PaymentFormValues>();

  const freeDepositAmount = Number(formik.values.freeDeposit) || 0;
  const isExceededDuePrice = freeDepositAmount > props.price.value;

  useBookingNavbar({
    processValidation: isExceededDuePrice ? onOpen : undefined,
  });

  return (
    <React.Fragment>
      <FreeDeposit {...props} price={{ ...props.price, value: withExceedancePrice }} />
      {isOpen && (
        <Modal closeLabel={t('common.close')} onClose={onClose}>
          <div className="flex flex-col p-25 text-sienna">
            <TemplateString
              template={t('booking.partners.banking.freeDeposit.maxAmountExceeded')}
              valuesClassName={{ value: 'font-bold text-16 text-sienna' }}
              values={{
                value: formatAmount(freeDepositAmount, currency, locale),
              }}
            />
          </div>
          <div>
            <div className="mx-20 mb-20 mt-15 flex justify-center gap-5 md:justify-end">
              <Button colorBg="transparent" colorBorder="black" onClick={onClose} size="medium">
                {t('common.cancel')}
              </Button>
              <Button
                className="text-12 font-bold"
                onClick={() => {
                  onClose();
                  validationCallback?.();
                }}
                size="medium"
              >
                {t('common.continue')}
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </React.Fragment>
  );
};
